import { Badge, IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SectionLabel, DetailSections } from '../shared/index.js';
import OwnerChip from '../../Team/OwnerChip.jsx';
import { formatTaskEntityType } from '../../../utils/textCase.js';
import { formatDate } from '../../../utils/detailFormat.js';
import { shortEventId } from '../../../utils/formatEventId.js';
import { ListChecks, Link2 } from 'lucide-react';

function entityBadgeVariant(entityType) {
  switch (String(entityType ?? '').toLowerCase()) {
    case 'change_request':
      return 'purple';
    case 'claim':
      return 'warning';
    case 'truth':
      return 'lightBlue';
    default:
      return 'neutral';
  }
}

function severityBadgeVariant(severityLabel) {
  switch (String(severityLabel ?? '').toLowerCase()) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'alert';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Assigned work item detail for interim `task` events (CT-5091/5093).
 *
 * Visual language: My Team / assignment layer — distinct from truth-promotion
 * cards (Proposal) but sharing the prototype's section rhythm (hero context,
 * linked entity card, message, assignee). Task is not an HTL Event Type rename;
 * it wraps another entity (`entityType` + `entityUuid`).
 */
function TaskBody({ event }) {
  const { t } = useTranslation('work-queues-app');
  const detail = event.detail ?? {};

  const message = event.situation ?? event.description ?? null;
  const entityType = formatTaskEntityType(detail.entityType, t);
  const entityUuid = detail.entityUuid ?? null;
  const entityRef = entityUuid ? shortEventId(entityUuid) : null;
  const severityLabel = detail.severityLabel ?? null;
  const updatedAt = formatDate(detail.updatedAt);

  const assignmentHero = (
    <div
      key="hero"
      className="rounded-xl border border-[#2173FF]/25 bg-gradient-to-br from-[#2173FF]/10 via-sky-500/5 to-transparent p-4"
    >
      <div className="flex items-start gap-3">
        <IconHighlight bgColor="LightBlue" iconColor="LightBlue" size="medium" icon={ListChecks} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-content">{t('task.assignmentTitle')}</p>
            <Badge variant="informative" size="small">
              {t('eventType.task')}
            </Badge>
            {severityLabel && (
              <Badge variant={severityBadgeVariant(severityLabel)} size="small">
                {severityLabel}
              </Badge>
            )}
          </div>
          {event.title && (
            <p className="mt-1 break-words text-[13px] font-medium leading-snug text-content">{event.title}</p>
          )}
          {updatedAt && (
            <p className="mt-1.5 text-[11px] text-content-faint">
              {t('task.lastUpdated', { date: updatedAt })}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const linkedEntity =
    entityType || entityUuid ? (
      <div key="entity">
        <SectionLabel>{t('task.linkedEntity')}</SectionLabel>
        <div className="flex items-start gap-3 rounded-lg border border-edge bg-surface-muted px-4 py-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface">
            <Link2 size={16} className="text-content-muted" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {entityType && (
                <Badge variant={entityBadgeVariant(detail.entityType)} size="small">
                  {t('task.linkedEntityShort', { entity: entityType })}
                </Badge>
              )}
              {entityRef && (
                <span className="font-mono text-[11px] text-content-faint">{entityRef}</span>
              )}
            </div>
            {entityUuid && (
              <p className="mt-1 break-all text-[11px] text-content-faint" title={entityUuid}>
                {entityUuid}
              </p>
            )}
            <p className="mt-2 text-[11px] leading-relaxed text-content-faint">{t('task.linkedEntityHint')}</p>
          </div>
        </div>
      </div>
    ) : null;

  const messageBlock = message ? (
    <div key="message">
      <SectionLabel>{t('task.detailMessage')}</SectionLabel>
      <p className="rounded-lg border border-edge bg-surface-muted px-3 py-2.5 text-[13px] leading-relaxed text-content-secondary">
        {message}
      </p>
    </div>
  ) : null;

  const assigneeBlock = (
    <div key="assignee">
      <SectionLabel>{t('task.assignee')}</SectionLabel>
      <div className="mt-1.5">
        <OwnerChip owner={event.owner} />
      </div>
    </div>
  );

  const sections = [assignmentHero, linkedEntity, messageBlock, assigneeBlock].filter(Boolean);

  if (!sections.length) {
    return (
      <p className="text-sm leading-relaxed text-content-secondary">{event.title ?? t('eventType.task')}</p>
    );
  }

  return <DetailSections sections={sections} />;
}

export default TaskBody;
